import { FC, useState } from 'react';
import {
  useGetScheduledCallsQuery,
  useGetCourseHearingsQuery,
  useGetCourseHearingQuery,
} from '@teachur/http-client';
import { ScheduleCard } from './ScheduleCard';
import { useVideoCallContext } from 'src/contexts/VideoCallProvider';
import { useSingleCourse } from '@new/features/courses/contexts/SingleCourseProvider';
import { Loader } from '@new/common/ui/Loader/Loader';
import { StudentSchedule } from '@new/features/schedule/components/StudentSchedule/StudentSchedule';
import { useHearingStatus } from '@new/features/courses/hooks/useHearingStatus';
import { useRequireSubscribed } from '@new/features/subscription/hooks/useRequireSubscribed';
import { HearingOverview } from './HearingOverview';
import { CourseHearingSubmissions } from './CourseHearingSubmissions/CourseHearingSubmissions';
import { PracticalHearingContent } from './PracticalHearingContent';

export const HearingContent: FC = () => {
  const [isScheduleMeetingModalOpen, setIsScheduleMeetingModalOpen] = useState(false);
  const [hasJoinedHearing, setHasJoinedHearing] = useState(false);

  const { course, activeStep } = useSingleCourse();
  const { openVideoCall } = useVideoCallContext();
  const requireSubscribed = useRequireSubscribed();

  const isPractical = !!course?.isPractical;

  const {
    data: scheduledCallsResponse,
    isLoading: isScheduledCallsLoading,
    refetch: refetchScheduledCalls,
  } = useGetScheduledCallsQuery(
    { courseId: course?.id },
    { enabled: activeStep === 'hearing' && !!course?.id && !isPractical }
  );

  const {
    data: courseHearingsResponse,
    isLoading: isCourseHearingsLoading,
    refetch: refetchCourseHearings,
  } = useGetCourseHearingsQuery(
    { courseId: course?.id },
    { enabled: activeStep === 'hearing' && !!course?.id && !isPractical }
  );

  const listHearingId = courseHearingsResponse?.data?.[0]?.id;
  const { data: fullCourseHearing, refetch: refetchFullCourseHearing } = useGetCourseHearingQuery(
    listHearingId,
    {
      enabled: activeStep === 'hearing' && !!listHearingId && !isPractical,
    }
  );

  const scheduledCalls = scheduledCallsResponse?.data ?? [];
  const isLocked = !course?.projectCompleted;
  const courseHearing = courseHearingsResponse?.data?.[0];
  const courseHearingDerivedStatus = courseHearing?.derivedStatus;

  const {
    latestScheduledCall,
    scheduledCallStatus,
    hearingStatus,
    isMissed,
    showScheduleMessage,
    showCompletedScheduleCard,
    canScheduleMeeting,
  } = useHearingStatus({
    coursePassed: !!course?.coursePassed,
    isLocked,
    scheduledCalls,
    hasJoinedHearing,
    courseHearingDerivedStatus,
    attempts: fullCourseHearing?.attempts,
    attemptsLoaded: fullCourseHearing !== undefined,
  });

  if (activeStep !== 'hearing' || !course) return null;

  if (isPractical) {
    return <PracticalHearingContent />;
  }

  if (isScheduledCallsLoading || isCourseHearingsLoading) {
    return <Loader showLogo={false} />;
  }

  const onScheduleClick = requireSubscribed(() => {
    setIsScheduleMeetingModalOpen(true);
  });

  const onClose = () => setIsScheduleMeetingModalOpen(false);

  const onScheduleSuccess = () => {
    setHasJoinedHearing(false);

    refetchScheduledCalls();
    refetchCourseHearings();
    refetchFullCourseHearing();
  };

  const onJoinHearing = requireSubscribed((videoCallId: string) => {
    setHasJoinedHearing(true);
    openVideoCall(videoCallId);
  });

  const latestAttempt = fullCourseHearing?.attempts?.find(
    (a) => a.id === latestScheduledCall?.id || a.videoCallId === latestScheduledCall?.videoCallId
  );
  const latestSubmissionStatus = latestAttempt?.hearingSubmission?.status;
  const isLatestCallReviewed =
    latestSubmissionStatus === 'approved' || latestSubmissionStatus === 'notYet';

  return (
    <>
      <HearingOverview
        isLocked={isLocked}
        canScheduleMeeting={canScheduleMeeting}
        scheduledCallStatus={scheduledCallStatus}
        hearingStatus={hearingStatus}
        showScheduleMessage={showScheduleMessage}
        isMissed={isMissed}
        onScheduleClick={onScheduleClick}
      />

      {latestScheduledCall &&
        (latestScheduledCall.status === 'pending' || latestScheduledCall.status === 'accepted') &&
        !isLatestCallReviewed && (
          <ScheduleCard
            call={latestScheduledCall}
            onJoinClick={
              latestScheduledCall.status === 'accepted' &&
              latestScheduledCall.videoCallId &&
              !showCompletedScheduleCard &&
              !isMissed
                ? onJoinHearing
                : undefined
            }
            canCancel={!showCompletedScheduleCard}
            overrideStatus={
              showCompletedScheduleCard ? 'completed' : isMissed ? 'missed' : undefined
            }
          />
        )}
      <CourseHearingSubmissions />

      <StudentSchedule
        isOpen={isScheduleMeetingModalOpen}
        onClose={onClose}
        courseId={course.id}
        onScheduleSuccess={onScheduleSuccess}
      />
    </>
  );
};
